import { IonCol } from '@ionic/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CalibrationCompleted } from '@/components/CalibrationCompleted/CalibrationCompleted';
import { Sensor } from '@/components/SensorCard/SensorCard';
import { SensorFlowModal } from '@/components/SensorFlowModal/SensorFlowModal';
import { SensorPlacement, SensorPlacementProps } from '@/components/SensorPlacement/SensorPlacement';

import { mergeClassNames } from '@/utils';
import Logger from '@/utils/CapcitorLog';

import { hubSensorService } from '@/controllers/sensorService';

import { useLowBatteryDevices } from '@/hooks/useLowBatteryDevices';
import { useSensors } from '@/hooks/useSensors';

import { SensorsState, useSensorsContext } from '@/contexts/SensorsContext';

import styles from './SensorScreenContent.module.css';

import { SensorCalibration } from '../SensorCalibration/SensorCalibration';

interface SensorScreenContentProps {
  isHubFlow: boolean;
  currentStep: number;
  selectedSensor?: Sensor;
  state: SensorsState;
  setAllSynced: (allSynced: boolean) => void;
}

const RECONNECT_COOLDOWN = 20;

interface IContentProps {
  currentStep: number;
  isHubFlow: boolean;
  placementSensorStates: Omit<SensorPlacementProps, 'isBlurred'>;
  selectedSensor?: Sensor;
  allConnected: boolean;
  allSynced: boolean;
  reconnectCooldown: number;
  hasCalibrationFailed: boolean;
  isCalibrationCompleted: boolean;
  isLoading: boolean;
  isReady: boolean;
  startCalibration: VoidFunction;
  setAllSynced: (value: boolean) => void;
  handleReconnect: VoidFunction;
}

const Content = ({
  currentStep,
  allConnected,
  isHubFlow,
  allSynced,
  reconnectCooldown,
  placementSensorStates,
  hasCalibrationFailed,
  isCalibrationCompleted,
  isLoading,
  isReady,
  startCalibration,
  setAllSynced,
  handleReconnect,
}: IContentProps) => {
  const { resetCalibration } = useSensorsContext();

  const handleResetCalibration = () => {
    resetCalibration();
  };

  switch (currentStep) {
    case 0:
      if (allConnected && (!isHubFlow || allSynced)) {
        return <CalibrationCompleted description='' title='Connection complete' />;
      }
      if (allConnected && isHubFlow) {
        return (
          <SensorFlowModal
            description='Allow up to 20 seconds for the sensors to sync'
            illustration='syncing'
            primaryButton={{
              label: 'Yes, sensors are synced',
              onClick: () => {
                setAllSynced(true);
              },
            }}
            secondaryButton={{
              disabled: reconnectCooldown > 0,
              label: reconnectCooldown === 0 ? 'Reconnect' : `Reconnect (${reconnectCooldown})`,
              onClick: handleReconnect,
              leadingIcon: 'reset',
            }}
            title='Make sure the sensor lights flash at the same time...'
          />
        );
      }

      return (
        <SensorFlowModal
          description='Turn on the sensors by pressing the power button. When the sensors are turned on, they will flash blue.'
          illustration='turnOn'
          title='Turn on the sensors...'
        />
      );
    case 1:
      return <SensorPlacement {...placementSensorStates} />;
    case 2:
      return (
        <SensorCalibration
          hasCalibrationFailed={hasCalibrationFailed}
          isCalibrationCompleted={isCalibrationCompleted}
          isLoading={isLoading}
          isReady={isReady}
          placementSensorStates={placementSensorStates}
          resetCalibration={handleResetCalibration}
          startCalibration={startCalibration}
        />
      );
    default:
      return null;
  }
};

export const SensorScreenContent = ({
  isHubFlow,
  currentStep,
  selectedSensor,
  state,
  setAllSynced,
}: SensorScreenContentProps) => {
  const { startCalibration } = useSensors(isHubFlow);
  const [reconnectCooldown, setReconnectCooldown] = useState(0);
  const reconnectCountdownInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    return () => {
      if (reconnectCountdownInterval.current) {
        clearInterval(reconnectCountdownInterval.current);
      }
    };
  }, []);

  // const { state, setAllSynced } = useSensorsContext();
  const { isCalibrating, allConnected, failedCalibrationSensors, allSuccessfullyCalibrated, allSynced } = state;

  const lowBatteryDevices = useLowBatteryDevices();

  const shouldAlignStart = [1, 2].includes(currentStep);
  const hasCalibrationFailed = !isCalibrating && !allSuccessfullyCalibrated && failedCalibrationSensors.length > 0;
  const isCalibrationCompleted = allSuccessfullyCalibrated && failedCalibrationSensors.length === 0;
  const isLoading = isCalibrating && !allSuccessfullyCalibrated;
  const isReady = !isCalibrating && !allSuccessfullyCalibrated;

  //TODO: Should be based on system from firebase
  const placementSensorStates = useMemo(() => {
    const base: Omit<SensorPlacementProps, 'isBlurred'> = {
      centerPelvis: 'hidden',
      leftPelvis: 'not_calibrated',
      leftThigh: 'not_calibrated',
      leftCalf: 'not_calibrated',
      rightPelvis: 'not_calibrated',
      rightThigh: 'not_calibrated',
      rightCalf: 'not_calibrated',
      leftFoot: isHubFlow ? 'not_calibrated' : 'hidden',
      rightFoot: isHubFlow ? 'not_calibrated' : 'hidden',
    };

    // if there's an error, set those specific sensor(s) to 'error'
    if (hasCalibrationFailed) {
      const failedSensorIds = state.failedSensors;
      const allSensors = state.sensorsInDatabase;

      return {
        ...base,
        ...failedSensorIds.reduce((acc, failedSensorId) => {
          const sensor = allSensors.find((sensor) => sensor.id === failedSensorId);
          if (sensor) {
            return { ...acc, [sensor.mappedSegment]: 'error' };
          }
          return acc;
        }, {}),
      };
    }

    // when selectedSensorId is defined, set that sensor id to selected
    if (selectedSensor && currentStep === 1) {
      return {
        ...base,
        [selectedSensor.segment]: 'selected',
      };
    }

    // If any value is selected, set it to 'not_calibrated'
    return Object.entries(base).reduce((acc, [key, value]) => {
      if (value === 'selected') {
        return { ...acc, [key]: 'not_calibrated' };
      }
      return { ...acc, [key]: value };
    }, base);
  }, [selectedSensor]);

  async function handleReconnect() {
    if (reconnectCountdownInterval.current) {
      clearInterval(reconnectCountdownInterval.current);
    }

    await hubSensorService.endSession();
    await hubSensorService.startSession();

    setReconnectCooldown(RECONNECT_COOLDOWN);

    reconnectCountdownInterval.current = setInterval(() => {
      setReconnectCooldown((prev) => {
        if (prev === 0) {
          if (reconnectCountdownInterval.current) {
            clearInterval(reconnectCountdownInterval.current);
          }
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }

  return (
    <IonCol
      className={mergeClassNames('ion-no-padding', styles.container, shouldAlignStart && styles.alignSelfStart)}
      size='4'>
      <Content
        allConnected={allConnected}
        allSynced={allSynced}
        currentStep={currentStep}
        handleReconnect={handleReconnect}
        hasCalibrationFailed={hasCalibrationFailed}
        isCalibrationCompleted={isCalibrationCompleted}
        isHubFlow={isHubFlow}
        isLoading={isLoading}
        isReady={isReady}
        placementSensorStates={placementSensorStates}
        reconnectCooldown={reconnectCooldown}
        setAllSynced={setAllSynced}
        startCalibration={startCalibration}
      />
    </IonCol>
  );
};
